import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { checkoutOwnerMatches } from "./cartCheckoutIdentity";

export const getCartRecoveryEligibility = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ locale: z.enum(["ar", "en"]) }).strict())
  .handler(async ({ context, data }) => {
    const { bootstrapRecoveryEligibility } = await import("./server/cartRecovery.server");
    const result = await bootstrapRecoveryEligibility(context.userId, data.locale);
    return { ownerId: context.userId, enabled: result.ok === true, eligible: result.eligible === true,
      retryable: !result.ok && ["setup_pending", "disabled_or_unverified", "verified_email_required"].includes(result.reason ?? ""),
      eligibilityGeneration: result.ok && result.eligible && typeof result.eligibilityGeneration === "string"
        ? result.eligibilityGeneration : null };
  });

export const recordCartRecoveryMutation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ initiatingUserId: z.string().uuid().nullable(),
    eligibilityGeneration: z.string().uuid().nullable(),
    cartId: z.string().min(1).max(500), country: z.string().regex(/^[A-Z]{2}$/).nullable(),
    lines: z.array(z.object({ variantId: z.string().regex(/^gid:\/\/shopify\/ProductVariant\/\d+$/),
      quantity: z.number().int().min(1).max(999) })).max(50),
    kind: z.enum(["mutation", "empty"]) }).strict())
  .handler(async ({ data, context }) => {
    if (data.initiatingUserId !== context.userId) return { ok: false, reason: "identity_changed" };
    const { recoveryOperation } = await import("./server/cartRecovery.server");
    const { recoveryFingerprint } = await import("./cartRecovery");
    return recoveryOperation(context.userId, data.kind === "empty" ? "empty" : "mutation", {
      cartId: data.cartId, country: data.country,
      eligibilityGeneration: data.eligibilityGeneration,
      fingerprint: recoveryFingerprint(data.country, data.lines), lines: data.lines,
    });
  });

export const cancelCartRecoveryCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ cartId: z.string().min(1).max(500), initiatingUserId: z.string().uuid() }).strict())
  .handler(async ({ data, context }) => {
    if (!checkoutOwnerMatches(data.initiatingUserId, context.userId)) {
      return { ownerId: context.userId, state: "unknown" as const, checkoutAllowed: false,
        recoveryCovered: false, reason: "identity_changed" };
    }
    const { recoveryCheckoutHandoff } = await import("./server/cartRecovery.server");
    return { ...await recoveryCheckoutHandoff(context.userId, data.cartId), ownerId: context.userId };
  });

export const quarantineCartRecovery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ cartId: z.string().min(1).max(500), eligibilityGeneration: z.string().uuid().nullable() }).strict())
  .handler(async ({ data, context }) => {
    const { recoveryOperation } = await import("./server/cartRecovery.server");
    return recoveryOperation(context.userId, "quarantine", data);
  });

export const getMyRecoveryEpisode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ episodeId: z.string().uuid() }).strict())
  .handler(async ({ data, context }) => {
    const { readRecoveryEpisode } = await import("./server/cartRecovery.server");
    return readRecoveryEpisode(context.userId, data.episodeId);
  });